import { parseISO, differenceInDays, addMonths, isBefore, isEqual, startOfWeek } from 'date-fns';
import { MONTH_ABBR } from './constants';
import type { ComparisonDataPoint, Granularity, SouscriptionEntry } from '../types';

/**
 * Stratégies d'agrégation Area chart — cœur du module (SPEC-PORT.md §8).
 *
 * Règle critique : l'axe X des courbes N/N-1 s'adapte à la période :
 *  - ≤ 14 jours → `day`
 *  - > 14 jours et ≤ 3 mois → `week`
 *  - > 3 mois → `month`
 *
 * Les 3 stratégies de matching N/N-1 sont DIFFÉRENTES et volontairement
 * non unifiées (elles répondent à des besoins distincts) :
 *  - `month` → match par `${yyyy-1}-${mm}`
 *  - `day`   → match par `MM-DD` (permet alignement jour-jour inter-années)
 *  - `week`  → match par index ordinal dans la période (1re semaine vs 1re)
 */

export function getGranularity(dateFrom: string, dateTo: string): Granularity {
  const from = parseISO(dateFrom);
  const to = parseISO(dateTo);
  const days = differenceInDays(to, from);
  if (days <= 14) return 'day';
  const threeMonthsLater = addMonths(from, 3);
  if (isBefore(to, threeMonthsLater) || isEqual(to, threeMonthsLater)) return 'week';
  return 'month';
}

export function getGranularityTitle(g: Granularity): string {
  return ({ day: 'journalière', week: 'hebdomadaire', month: 'mensuelle' } as const)[g];
}

export function getGranularitySuffix(g: Granularity): string {
  return ({ day: 'par jour', week: 'par semaine', month: 'par mois' } as const)[g];
}

/**
 * Agrège une liste d'entries par granularité. La clé (`rawKey`) est utilisée
 * pour le matching N/N-1, le `label` est l'affichage humain.
 *
 * Basée sur `dateField` :
 *  - `dateSouscription` par défaut
 *  - `dateEffet` si le toggle UI l'indique
 */
export function aggregate(
  entries: SouscriptionEntry[],
  granularity: Granularity,
  dateField: 'dateSouscription' | 'dateEffet' = 'dateSouscription',
): Array<{ rawKey: string; label: string; total: number }> {
  const map = new Map<string, { label: string; total: number }>();

  for (const e of entries) {
    const date = e[dateField];
    if (!date) continue;
    let rawKey: string;
    let label: string;

    if (granularity === 'month') {
      rawKey = date.substring(0, 7); // YYYY-MM
      const [y, m] = rawKey.split('-').map(Number);
      label = `${MONTH_ABBR[(m ?? 1) - 1]} ${y}`;
    } else if (granularity === 'day') {
      rawKey = date; // YYYY-MM-DD complet
      const [, mStr, dStr] = date.split('-');
      label = `${Number(dStr)} ${MONTH_ABBR[Number(mStr) - 1]}`;
    } else {
      // week
      const monday = startOfWeek(parseISO(date), { weekStartsOn: 1 });
      const y = monday.getFullYear();
      const mIdx = monday.getMonth();
      const d = monday.getDate();
      const mm = String(mIdx + 1).padStart(2, '0');
      const dd = String(d).padStart(2, '0');
      rawKey = `${y}-${mm}-${dd}`; // lundi début de semaine
      const semaineDuMois = Math.ceil(d / 7);
      label = `${MONTH_ABBR[mIdx]} S${semaineDuMois}`;
    }

    const existing = map.get(rawKey);
    if (existing) {
      existing.total += 1;
    } else {
      map.set(rawKey, { label, total: 1 });
    }
  }

  return Array.from(map.entries())
    .map(([rawKey, v]) => ({ rawKey, ...v }))
    .sort((a, b) => a.rawKey.localeCompare(b.rawKey));
}

/**
 * Aligne les séries N et N-1 selon la stratégie de matching propre à la
 * granularité. Renvoie un tableau plat prêt pour Recharts.
 *
 * Quand un bucket n'a pas d'équivalent N-1 → `totalPrev = null` → gap
 * dans la courbe N-1 géré par `connectNulls` côté Recharts.
 */
export function buildComparisonData(
  currentEntries: SouscriptionEntry[],
  prevEntries: SouscriptionEntry[],
  granularity: Granularity,
  dateField: 'dateSouscription' | 'dateEffet' = 'dateSouscription',
): ComparisonDataPoint[] {
  const current = aggregate(currentEntries, granularity, dateField);
  const prev = aggregate(prevEntries, granularity, dateField);

  if (granularity === 'month') {
    const prevByKey = new Map(prev.map((p) => [p.rawKey, p.total]));
    return current.map((item) => {
      const [year, mm] = item.rawKey.split('-');
      const prevKey = `${parseInt(year!, 10) - 1}-${mm}`;
      const totalPrev = prevByKey.get(prevKey);
      return {
        label: item.label,
        total: item.total,
        totalPrev: totalPrev ?? null,
        rawKey: item.rawKey,
      };
    });
  }

  if (granularity === 'day') {
    // Match par MM-DD (substring 5-10 de YYYY-MM-DD)
    const prevByMmDd = new Map(prev.map((p) => [p.rawKey.substring(5), p.total]));
    return current.map((item) => {
      const mmDd = item.rawKey.substring(5);
      const totalPrev = prevByMmDd.get(mmDd);
      return {
        label: item.label,
        total: item.total,
        totalPrev: totalPrev ?? null,
        rawKey: item.rawKey,
      };
    });
  }

  // Week : match par index ordinal
  return current.map((item, index) => ({
    label: item.label,
    total: item.total,
    totalPrev: prev[index]?.total ?? null,
    rawKey: item.rawKey,
  }));
}

/**
 * Prorata d'une cotisation annuelle à partir de la date d'effet du contrat,
 * jusqu'au 31 décembre de l'année de prise d'effet.
 *
 * Formule : `cotisationAnnuelleTTC × (joursRestants / 365)` où
 * `joursRestants = 31-déc(annéeDeDateEffet) - dateEffet + 1` (inclus).
 *
 * Exemples :
 *   - dateEffet = 2026-01-01 → prorata ≈ cotisation × 365/365 = cotisation
 *   - dateEffet = 2026-07-01 → prorata ≈ cotisation × 184/365 ≈ 50 %
 *   - dateEffet = 2026-12-31 → prorata ≈ cotisation × 1/365 ≈ 0
 *
 * On conserve le dénominateur 365 (et non 366 sur années bissextiles) pour
 * rester aligné avec la convention métier Adélie sur l'année commerciale.
 */
export function computeProrataToYearEnd(dateIso: string, cotisationAnnuelleTTC: number): number {
  if (cotisationAnnuelleTTC <= 0) return 0;
  const [y, m, d] = dateIso.split('-').map(Number);
  if (!y || !m || !d) return 0;
  const effet = Date.UTC(y, m - 1, d);
  const yearEnd = Date.UTC(y, 11, 31);
  const daysRemaining = Math.max(0, Math.floor((yearEnd - effet) / 86_400_000) + 1);
  return (cotisationAnnuelleTTC * daysRemaining) / 365;
}
